/* ============================================================
   global-scripts.js — SPORTIV
   Shared behaviour, driven by data attributes:
   [data-dialog-open="id"]       opens <dialog id="id"> as a modal
   [data-dialog-close]           closes the dialog it sits in
   dialog[data-dialog-autoopen]  opens on load (form posted / errors)
   dialog[data-dialog-hash="x"]  opens when the URL hash is #x
   form[data-klaviyo-form]       Klaviyo list embed form, submitted over AJAX
     [data-klaviyo-success]      shown once subscribed
     [data-klaviyo-card]         height held on submit so the success state doesn't collapse it
   [data-bg-video]               plays its muted video while on screen
     video[data-src]             source set on first view (MP4 or HLS .m3u8)     [data-hls-lib="url"]        hls.js, loaded only where HLS isn't native
   ============================================================ */

const BREAKPOINTS = { mobile: 768, tablet: 1024 };
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function openDialog(dialog) {
  if (!(dialog instanceof HTMLDialogElement)) return;
  dialog.removeAttribute('data-dialog-closing');
  if (!dialog.open) dialog.showModal();
}

/* Plays the exit animation (base.css), then closes. */
function closeDialog(dialog) {
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open || dialog.hasAttribute('data-dialog-closing')) return;
  if (prefersReducedMotion()) {
    dialog.close();
    return;
  }

  dialog.setAttribute('data-dialog-closing', '');
  const animations = dialog.getAnimations({ subtree: true }).map((animation) => animation.finished);
  Promise.allSettled(animations).then(() => {
    if (!dialog.hasAttribute('data-dialog-closing')) return; // reopened mid-close
    dialog.close();
    dialog.removeAttribute('data-dialog-closing');
  });
}

function initDialogs() {
  if (document.documentElement.dataset.dialogsInit) return;
  document.documentElement.dataset.dialogsInit = 'true';

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-dialog-open]');
    if (opener) {
      openDialog(document.getElementById(opener.getAttribute('data-dialog-open')));
      return;
    }

    if (target.closest('[data-dialog-close]')) {
      closeDialog(target.closest('dialog'));
      return;
    }

    if (target instanceof HTMLDialogElement) closeDialog(target);
  });

  // Escape: animate out instead of the instant native close. `cancel` doesn't bubble.
  document.addEventListener('cancel', (event) => {
    if (!(event.target instanceof HTMLDialogElement) || !event.target.matches('[data-dialog]')) return;
    event.preventDefault();
    closeDialog(event.target);
  }, true);

  const openFromHash = () => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    openDialog(document.querySelector(`dialog[data-dialog-hash="${CSS.escape(hash)}"]`));
  };

  window.addEventListener('hashchange', openFromHash);
  openFromHash();

  for (const dialog of document.querySelectorAll('dialog[data-dialog-autoopen]')) openDialog(dialog);
}

let hlsLoader;
const videoSources = new WeakMap();
const visibleVideos = new WeakSet();

function loadHls(src) {
  hlsLoader ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(window.Hls);
    script.onerror = reject;
    document.head.append(script);
  });
  return hlsLoader;
}

/* Sets a [data-src] video's source on first view. HLS plays natively where supported, otherwise through hls.js. */
function attachVideoSource(wrapper, video) {
  const src = video.dataset.src;
  if (!src) return Promise.resolve();
  if (videoSources.has(video)) return videoSources.get(video);

  const isHls = new URL(src, location.href).pathname.endsWith('.m3u8');
  let ready;
  if (!isHls || video.canPlayType('application/vnd.apple.mpegurl')) {
    video.src = src;
    ready = Promise.resolve();
  } else {
    ready = loadHls(wrapper.dataset.hlsLib).then((Hls) => {
      if (!Hls?.isSupported()) return;
      const hls = new Hls({ capLevelToPlayerSize: true });
      hls.loadSource(src);
      hls.attachMedia(video);
      return new Promise((resolve) => hls.once(Hls.Events.MANIFEST_PARSED, resolve));
    });
  }

  videoSources.set(video, ready);
  return ready;
}

function initBgVideos() {
  if (prefersReducedMotion()) return;

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const video = entry.target.querySelector('video');
      if (!video) continue;
      if (entry.isIntersecting) {
        visibleVideos.add(video);
        attachVideoSource(entry.target, video)
          .then(() => visibleVideos.has(video) && video.play())
          .catch(() => {});
      } else {
        visibleVideos.delete(video);
        video.pause();
      }
    }
  });

  for (const wrapper of document.querySelectorAll('[data-bg-video]:not([data-bg-video-init])')) {
    wrapper.setAttribute('data-bg-video-init', '');
    observer.observe(wrapper);
  }
}

let klaviyoLoader;

function loadKlaviyoSubscribe() {
  klaviyoLoader ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://www.klaviyo.com/media/js/public/klaviyo_subscribe.js';
    script.onload = () => resolve(window.KlaviyoSubscribe);
    script.onerror = reject;
    document.head.append(script);
  });
  return klaviyoLoader;
}

/* Identifying the subscriber to klaviyo.js (Klaviyo app embed) links this browser
   to the profile, so Klaviyo sets location from IP the same way its own forms do. */
function identifyKlaviyo(form) {
  const email = form.querySelector('[name="email"]')?.value.trim();
  if (!email || !window.klaviyo) return;
  const firstName = form.querySelector('[name="$first_name"]')?.value.trim();
  window.klaviyo.identify(firstName ? { email, first_name: firstName } : { email });
}

function initKlaviyoForms() {
  const forms = document.querySelectorAll('form[data-klaviyo-form]:not([data-klaviyo-init])');
  if (!forms.length) return;
  for (const form of forms) {
    form.setAttribute('data-klaviyo-init', '');
    form.addEventListener('submit', () => {
      const card = form.querySelector('[data-klaviyo-card]');
      if (card && !card.style.minHeight) card.style.minHeight = `${card.offsetHeight}px`;
    }, true);
  }

  loadKlaviyoSubscribe()
    .then((KlaviyoSubscribe) => {
      for (const form of forms) {
        KlaviyoSubscribe.attachToForms(`#${CSS.escape(form.id)}`, {
          hide_form_on_success: true,
          extra_properties: {
            $source: '$embed',
            $method_type: 'Landing Page Form',
            $method_id: 'embed',
          },
          success: () => {
            const message = form.querySelector('[data-klaviyo-success]');
            if (message) message.style.display = '';
            const error = form.querySelector('.error_message');
            if (error) error.style.display = 'none';
            identifyKlaviyo(form);
            // Send the event to Google Analytics 4
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'generate_lead', {
                form_name: 'waitlist'
              });
            }
          },
        });
      }
    })
    .catch(() => {});
}

function init() {
  initDialogs();
  initBgVideos();
  initKlaviyoForms();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

document.addEventListener('shopify:section:load', init);
